import { MortgageNoteInputs, MortgageNoteCalculations } from '../types';

/**
 * JavaScript implementation of Excel's RATE function
 * Calculates the interest rate per period of an annuity
 *
 * @param nper - Number of periods
 * @param pmt - Payment made each period
 * @param pv - Present value (negative for cash outflow)
 * @param fv - Future value (default 0)
 * @param type - When payments are due (0 = end of period, 1 = beginning)
 * @param guess - Initial guess for the rate (default 0.1)
 * @returns The interest rate per period
 */
export function calculateRate(
  nper: number,
  pmt: number,
  pv: number,
  fv: number = 0,
  type: number = 0,
  guess: number = 0.1
): number {
  const maxIterations = 100;
  const epsilon = 1e-6;
  let rate = guess;

  for (let i = 0; i < maxIterations; i++) {
    if (Math.abs(rate) < epsilon) {
      rate = epsilon; // Avoid division by zero
    }

    const f =
      pv * Math.pow(1 + rate, nper) +
      pmt * (1 + rate * type) * ((Math.pow(1 + rate, nper) - 1) / rate) +
      fv;

    const df =
      nper * pv * Math.pow(1 + rate, nper - 1) +
      pmt *
        (1 + rate * type) *
        ((nper * Math.pow(1 + rate, nper - 1) * rate -
          (Math.pow(1 + rate, nper) - 1)) /
          (rate * rate));

    const newRate = rate - f / df;

    if (Math.abs(newRate - rate) < epsilon) {
      return newRate;
    }

    rate = newRate;
  }

  return rate;
}

/**
 * Calculates all mortgage note metrics based on user inputs
 */
export function calculateMortgageNote(
  inputs: MortgageNoteInputs
): MortgageNoteCalculations {
  const {
    listedPurchasePrice,
    tradeDeskFee,
    monthlyServicingFee,
    titlingFees,
    otherOneTimeFee,
    otherMonthlyFee,
    principalAndInterest,
    paymentsRemaining,
    desiredPurchasePrice,
  } = inputs;

  // Calculate Purchase Price (H column in Excel)
  const purchasePrice =
    listedPurchasePrice +
    tradeDeskFee +
    monthlyServicingFee * paymentsRemaining +
    titlingFees +
    otherOneTimeFee +
    otherMonthlyFee * paymentsRemaining;

  // Monthly Income (L column) - same as P&I
  const monthlyIncome = principalAndInterest;

  // Annual Income (M column)
  const annualIncome = 12 * principalAndInterest;

  // Total Income (N column)
  const totalIncome = monthlyIncome * paymentsRemaining;

  // Years (R column)
  const years = paymentsRemaining / 12;

  // Annualized ROI (O column)
  const annualizedROI =
    Math.pow(totalIncome / purchasePrice, 1 / years) - 1;

  // ROI (P column)
  const roi = (totalIncome - purchasePrice) / purchasePrice;

  // IRR (Q column) - using RATE function
  const irr = calculateRate(
    paymentsRemaining,
    principalAndInterest,
    -purchasePrice,
    0,
    0
  ) * 12;

  // Desired Purchase Price + Fees (T column)
  const desiredPurchasePriceWithFees =
    (desiredPurchasePrice || listedPurchasePrice) +
    tradeDeskFee +
    monthlyServicingFee * paymentsRemaining +
    titlingFees +
    otherOneTimeFee +
    otherMonthlyFee * paymentsRemaining;

  // Discount (U column)
  const discount = 1 - (desiredPurchasePrice || listedPurchasePrice) / listedPurchasePrice;

  // Discounted Annualized ROI (V column)
  const discountedAnnualizedROI =
    Math.pow(totalIncome / desiredPurchasePriceWithFees, 1 / years) - 1;

  // Discounted ROI (W column)
  const discountedROI =
    (totalIncome - desiredPurchasePriceWithFees) / desiredPurchasePriceWithFees;

  // Discounted IRR (X column)
  const discountedIRR = calculateRate(
    paymentsRemaining,
    principalAndInterest,
    -desiredPurchasePriceWithFees,
    0,
    0
  ) * 12;

  // Actual Profit (Y column)
  const actualProfit = totalIncome - desiredPurchasePriceWithFees;

  // Annual Profit (Z column)
  const annualProfit = (actualProfit / paymentsRemaining) * 12;

  return {
    purchasePrice,
    monthlyIncome,
    annualIncome,
    totalIncome,
    annualizedROI,
    roi,
    irr,
    years,
    desiredPurchasePriceWithFees,
    discount,
    discountedAnnualizedROI,
    discountedROI,
    discountedIRR,
    actualProfit,
    annualProfit,
  };
}

/**
 * Formats a number as currency
 */
export function formatCurrency(value: number): string {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return '---';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Formats a number as percentage
 */
export function formatPercentage(value: number, decimals: number = 3): string {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return '---';
  }
  return `${(value * 100).toFixed(decimals)}%`;
}

